// Feedback #833 (Gabriel, 2026-09-24) — "my two 'unposted' entries do not
// have an option to post."
//
// The entries were postable server-side the whole time (PUT
// /api/time-entries/:id/post).  What was missing was the AFFORDANCE: once an
// entry was reopened in the timer (resume mode), the Save & Post button was
// hidden, and the resume save is a plain PUT that deliberately never touches
// `status` (so an already-posted entry is never demoted — adversarial-review
// fix #3).  An attorney who reopens their entry, adds time, and saves three
// times never gets a way to post it from the timer.
//
// This module is the single source of truth for "save a reopened entry, and
// optionally post it", shared by the Electron pop-out timer (TimerWidget.js)
// and the in-window timer popup (Layout.js) so the two cannot drift, plus the
// one-click post used by the Time Entries tables and the post-save banner,
// and the seconds → billable-hours rounding both timers save with.
//
// Pure apart from the injected `api` client, so it is unit-testable without
// a browser or a network.

import { DEFAULT_FIRM_TZ, firmTz, utcToLocalInput } from '../utils/eventTime'

// ── start_time (firm decision 2026-10-15: "start time, auto-filled") ──────
// time_entries.start_time is the FIRM-LOCAL wall-clock time of day the work
// began, to the minute; the day is `date`.  The API takes 'HH:MM' (or
// 'HH:MM:SS', seconds dropped), '' / null for "not recorded", and answers
// 'HH:MM' or null — except where a route returns `te.*` (GET
// /api/matters/:id), which carries Postgres's 'HH:MM:SS'.  These helpers are
// the one place the client reads, writes and shows it: the timer form, the
// Break / Lunch buttons, the manual form, the CSV import and the tables.
const START_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/

// A stored or typed start time → 'HH:MM'; '' for empty (null / undefined /
// ''); null for anything the API would refuse ('9:05', '24:00', '9am', a
// number, an ISO timestamp).
export function normalizeStartTime (v) {
  if (v == null || v === '') return ''
  if (typeof v !== 'string') return null
  const m = START_TIME_RE.exec(v)
  return m ? `${m[1]}:${m[2]}` : null
}

// The FIRM-local time of day of an instant, 'HH:MM' (seconds dropped) —
// never the browser's zone: a staff member working from Taipei who clicks
// Break at 9:15 Pacific records 09:15.  '' for an invalid instant; an
// unknown zone falls back to the firm default rather than throwing.
export function firmTimeOfDay (at = Date.now(), tz = firmTz()) {
  const d = at instanceof Date ? at : new Date(Number(at))
  if (Number.isNaN(d.getTime())) return ''
  let local
  try { local = utcToLocalInput(d, tz || DEFAULT_FIRM_TZ) } catch { local = utcToLocalInput(d, DEFAULT_FIRM_TZ) }
  return local ? local.slice(11, 16) : ''
}

// '14:05' / '14:05:00' → '2:05 PM'; '' for empty or unreadable.
export function formatStartTime (v) {
  const s = normalizeStartTime(typeof v === 'string' ? v.trim() : v)
  if (!s) return ''
  const [h, m] = s.split(':').map(Number)
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

const TWELVE_HOUR_RE = /^(\d{1,2})(?::([0-5]\d))?(?::[0-5]\d(?:\.\d+)?)?\s*([ap])\.?\s*m?\.?$/i
const TWENTY_FOUR_HOUR_RE = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/
const DATE_THEN_TIME_RE = /^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}(?:[ T]+|,\s*)(.+)$/
const pad2 = (n) => String(n).padStart(2, '0')

// An imported spreadsheet cell (the CSV / XLSX "Start time" column) →
// 'HH:MM', '' for a blank cell, or null when it cannot be read (the row is
// then refused, never saved with a guessed time).  Reads 24-hour '9:05' /
// '14:30' / '14:30:59', 12-hour '9:05 AM' / '2:30 pm' / '9am', a date and
// time ('2026-10-14 09:15', '10/14/2026 9:15 AM' — the time part), and
// Excel's fraction of a day (0.375 = 9:00).  A value with a zone ('…Z',
// '+08:00') is refused: the column is the firm's wall clock, not an instant.
export function parseStartTimeCell (v) {
  if (v == null) return ''
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null
    // A spreadsheet library builds time cells from local fields.
    return `${pad2(v.getHours())}:${pad2(v.getMinutes())}`
  }
  if (typeof v === 'number') {
    if (!Number.isFinite(v) || v < 0) return null
    const fraction = v - Math.floor(v)
    // A whole day number (a date serial, or a bare "9") carries no time.
    if (v >= 1 && fraction === 0) return null
    const minutes = Math.min(1439, Math.round(fraction * 1440))
    return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`
  }
  const s = String(v).trim()
  if (s === '') return ''
  const dated = DATE_THEN_TIME_RE.exec(s)
  if (dated) return parseStartTimeCell(dated[1])
  let m = TWENTY_FOUR_HOUR_RE.exec(s)
  if (m) {
    const h = Number(m[1])
    return h <= 23 ? `${pad2(h)}:${m[2]}` : null
  }
  m = TWELVE_HOUR_RE.exec(s)
  if (m) {
    const h12 = Number(m[1])
    if (h12 < 1 || h12 > 12) return null
    const h = (h12 % 12) + (m[3].toLowerCase() === 'p' ? 12 : 0)
    return `${pad2(h)}:${m[2] || '00'}`
  }
  return null
}

// Seconds of work → billable hours, for both timers (Layout.js in-page popup
// and TimerWidget.js pop-outs): rounded UP to the next 0.1 h, with a 0.1 h
// minimum on any non-zero work — the server's roundBillableHours()
// (api/src/routes/timeEntries.js) and utils/format.js roundBillable() apply
// the same rule.  Never round to nearest: 8 min (0.133 h) bills 0.2, not
// 0.1, and the server's ceil cannot recover a tenth the client dropped.
// Works in whole seconds (callers already pass integers) and in 360-second
// tenths so float noise cannot push an exact tenth (720 s = 0.2 h) up a step.
export function billableHoursFromSeconds (seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.max(1, Math.ceil(Math.round(n) / 360)) / 10
}

export const POSTED_MSG = 'Time entry posted!'
export const PENDING_REVIEW_MSG = 'Time entry submitted for review — waiting on your reviewer.'

// Message shown after a successful post, branching on the review queue.
// A user flagged `time_review_required` gets status='posted' +
// review_status='pending_review' — the entry is in their reviewer's queue,
// not finalized, and saying "posted" would overstate it.
export function postedMessage (reviewStatus) {
  return reviewStatus === 'pending_review' ? PENDING_REVIEW_MSG : POSTED_MSG
}

// Should we call /post after the resume PUT?  Only when the entry is still
// unposted.  `null`/`undefined` (an API response that did not echo status)
// is treated as "unknown → try"; the server refuses an already-posted entry
// with a 400 rather than double-posting, so trying is safe.  'posted' and
// 'billed' are never re-posted.
export function needsPost (status) {
  return status !== 'posted' && status !== 'billed'
}

function errorText (err, fallback) {
  return err?.response?.data?.error || err?.message || fallback
}

// POST one entry.  Returns { status, review_status }.  Throws on failure so
// callers can surface the server's message (e.g. "Cannot post: Please add a
// description…", "You can only post your own time entries").
export async function postTimeEntry (api, entryId) {
  const res = await api.put(`/api/time-entries/${entryId}/post`)
  const row = res?.data || {}
  return {
    status: row.status || 'posted',
    review_status: row.review_status ?? null
  }
}

// Save a reopened (resumed) entry.  The PUT body never carries `status` or
// `billed` — that protection is unchanged and applies to BOTH Save Entry and
// Save & Post.  Posting is a separate, explicit /post call made only when
// `post` is true AND the entry is not already posted.
//
// Returns { id, status, review_status, justPosted, postError, updated_at,
// alreadySaved }.
//   - The PUT failing throws (nothing was saved; caller shows the error).
//   - The /post failing does NOT throw: the hours WERE saved, so the caller
//     must report "saved but not posted" rather than "error saving", which
//     would invite a retry that double-counts nothing but misleads the user.
//
// Resume version check (timers spec, amendment 9, 2026-10-14): a resume slot
// remembers the entry's updated_at from when it was opened; pass it as
// `expectedUpdatedAt` and the PUT carries expected_updated_at, which the
// server answers with 409 when the entry was changed elsewhere since (absent =
// the old unconditional PUT).  On that 409 the entry is re-read:
//   - it already holds EVERYTHING this PUT would write — the hours and every
//     field the body carries (description, activity code, billing status,
//     phase, date, start time, and the rate when one is sent): our own
//     earlier PUT landed
//     and only the response was lost → treated as saved (`alreadySaved:
//     true`), and the optional post still runs;
//   - otherwise → throws an Error with code 'entry_changed' whose
//     response.data.error is ENTRY_CHANGED_MSG, so the caller keeps the timer
//     paused and shows that line instead of overwriting someone's edit.
//   Hours alone are not enough (post-code review panel, 2026-10-14): a resume
//   that changed only the description, while someone else changed the billing
//   status, would otherwise be reported saved and its slot removed with the
//   description never written.
export const ENTRY_CHANGED_MSG = 'This entry changed since you resumed it'

const sameHours = (a, b) => {
  const x = Number(a)
  const y = Number(b)
  return Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < 0.001
}

const textOf = (v) => (v == null ? '' : String(v))
const phaseOf = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}

// Does the entry as re-read already hold everything `body` + hours would
// write?  Only the fields the PUT actually sends are compared (an absent rate
// or phase leaves the stored one alone, so it cannot differ).  The date is the
// YYYY-MM-DD prefix (the API serialises a DATE as midnight UTC).
export function entryHoldsPut (current, body, hoursStr) {
  if (!current || typeof current !== 'object') return false
  const b = body || {}
  if (!sameHours(current.hours, hoursStr)) return false
  if ('description' in b && textOf(current.description) !== textOf(b.description)) return false
  if ('activity_code' in b && textOf(current.activity_code) !== textOf(b.activity_code)) return false
  if ('billing_status' in b && textOf(current.billing_status) !== textOf(b.billing_status)) return false
  if ('matter_phase_id' in b && phaseOf(current.matter_phase_id) !== phaseOf(b.matter_phase_id)) return false
  if ('date' in b && b.date != null && textOf(current.date).slice(0, 10) !== textOf(b.date).slice(0, 10)) return false
  if ('start_time' in b && (normalizeStartTime(current.start_time) || '') !== (normalizeStartTime(b.start_time) || '')) return false
  if ('rate' in b && b.rate != null && b.rate !== '') {
    const x = Number(current.rate)
    const y = Number(b.rate)
    if (!(Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < 0.005)) return false
  }
  return true
}

export async function saveResumedEntry (api, entryId, form, hoursStr, { post = false, expectedUpdatedAt = null } = {}) {
  const { status: _s, billed: _b, ...safeForm } = form || {}
  const body = { ...safeForm, hours: hoursStr }
  if (expectedUpdatedAt) body.expected_updated_at = expectedUpdatedAt
  let row
  let alreadySaved = false
  try {
    const res = await api.put(`/api/time-entries/${entryId}`, body)
    row = res?.data || {}
  } catch (err) {
    if (!expectedUpdatedAt || err?.response?.status !== 409) throw err
    let current = null
    try {
      const got = await api.get(`/api/time-entries/${entryId}`)
      current = got?.data || null
    } catch { current = null }
    if (entryHoldsPut(current, safeForm, hoursStr)) {
      row = current
      alreadySaved = true
    } else {
      const changed = new Error(ENTRY_CHANGED_MSG)
      changed.code = 'entry_changed'
      changed.current = current
      changed.response = { status: 409, data: { error: ENTRY_CHANGED_MSG } }
      throw changed
    }
  }
  let status = row.status ?? null
  let reviewStatus = row.review_status ?? null
  let justPosted = false
  let postError = null
  if (post && needsPost(status)) {
    try {
      const posted = await postTimeEntry(api, entryId)
      status = posted.status
      reviewStatus = posted.review_status
      justPosted = true
    } catch (err) {
      postError = errorText(err, 'The entry could not be posted.')
    }
  }
  return { id: entryId, status, review_status: reviewStatus, justPosted, postError, updated_at: row.updated_at ?? null, alreadySaved }
}

// The success line after Save Entry / Save & Post, for both new and resumed
// entries.  `saved` is persistEntry's return value.
export function saveResultMessage ({ isResume, willPost, saved }) {
  if (!willPost) return isResume ? 'Time entry updated!' : 'Time entry saved!'
  if (!isResume) return postedMessage(saved?.review_status)
  if (saved?.postError) return 'Time entry updated — but NOT posted.'
  if (saved?.justPosted) return postedMessage(saved?.review_status)
  // Save & Post on an entry that was already posted: nothing to post.
  return 'Time entry updated (it was already posted).'
}

// Error line to pair with a failed post after a successful save.
export function postFailureText (postError) {
  return postError
    ? `Your hours were saved, but the entry was not posted: ${postError}`
    : ''
}

// Can this user post this entry from a list row?  Mirrors the server gate on
// PUT /:id/post (owner or admin; still unposted; not billed) so the button is
// only shown where the click can succeed.  Kept identical for the Today and
// Unposted tables — the drift between those two (Today had NO post control
// at all) is part of what feedback #833 reported.
export function canPostEntry (entry, user) {
  if (!entry || !user) return false
  if (entry.status !== 'unposted' || entry.billed) return false
  const isAdmin = user.role === 'admin' || user.permissions?.is_admin === true
  return isAdmin || entry.user_id === user.id
}
